// src/content/consumables.ts
//
// Phase 15 (CONSUMABLE-04, CONSUMABLE-05, CONSUMABLE-06): consumable
// catalogue. Effect implementations live in src/systems/consumables.ts.
// Pricing lives in src/content/prices.ts (plan 15-06).

import type { ConsumableDef, SlotNumber } from '../state/types';

export const CONSUMABLES: readonly ConsumableDef[] = Object.freeze([
  // ── Money-class (plan 15-02) ─────────────────────────────────────────
  {
    defId: 'double_or_nothing',
    name: 'Double or Nothing',
    description:
      '50% chance to double your money (max $40); keeps current money on the other 50%. Uses the run seed — not luck.',
    rarity: 'uncommon',
    effect: { type: 'doubleOrNothing' },
  },
  {
    defId: 'loose_change',
    name: 'Loose Change',
    description: 'Gain $5.',
    rarity: 'common',
    effect: { type: 'looseChange' },
  },
  {
    defId: 'compound',
    name: 'Compound',
    description: "Next round's interest cap is raised by $5.",
    rarity: 'uncommon',
    effect: { type: 'compound' },
  },
  // ── Cell-mutator defs (plan 15-03) ───────────────────────────────────────
  // Note: cellIdx / cellIdxA/B are placeholder defaults (0). The UI (plan 15-06)
  // always supplies an effectOverride with real indices via useConsumable's
  // optional 4th parameter. The def serves as catalogue metadata only.
  {
    defId: 'swap_cells',
    name: 'Swap Cells',
    description: 'Swap two board cells (e.g. move a number to a different position).',
    rarity: 'common',
    effect: { type: 'swapCells', cellIdxA: 0, cellIdxB: 0 },
  },
  {
    defId: 'flip_color',
    name: 'Flip Color',
    description: "Flip a number cell's color (red ↔ black). No effect on green or outside bets.",
    rarity: 'common',
    effect: { type: 'flipColor', cellIdx: 0 },
  },
  {
    defId: 'bleach',
    name: 'Bleach',
    description: 'Make a number cell colorless — it counts as both red and black.',
    rarity: 'uncommon',
    effect: { type: 'bleach', cellIdx: 0 },
  },
  {
    defId: 'merge',
    name: 'Merge',
    description: 'Merge two adjacent cells. The surviving cell pays double.',
    rarity: 'rare',
    effect: { type: 'merge', cellIdxA: 0, cellIdxB: 0 },
  },
  // ── Number-deck mutators + loaded_ball (plan 15-04) ─────────────────────
  {
    defId: 'skip_slot',
    name: 'Skip Slot',
    description: 'Remove a number from the wheel permanently (min 12 numbers, 0 locked).',
    rarity: 'uncommon',
    effect: { type: 'skipSlot', number: 1 as SlotNumber },
  },
  {
    defId: 'mint',
    name: 'Mint',
    description: 'Add an extra copy of a number to the wheel (max 50 entries).',
    rarity: 'common',
    effect: { type: 'mint', number: 1 as SlotNumber },
  },
  {
    defId: 'loaded_ball',
    name: 'Loaded Ball',
    description: 'Force the wheel to land on a chosen number this spin.',
    rarity: 'rare',
    effect: { type: 'loadedBall', number: 1 as SlotNumber },
  },
  {
    defId: 'shuffle_columns',
    name: 'Shuffle Columns',
    description: 'Randomly shuffle the numbers within each column of the board.',
    rarity: 'uncommon',
    effect: { type: 'shuffleColumns' },
  },
  {
    defId: 'shuffle_rows',
    name: 'Shuffle Rows',
    description: 'Randomly shuffle the numbers within each row of the board.',
    rarity: 'rare',
    effect: { type: 'shuffleRows' },
  },
  // ── Cell-boost mutators ──────────────────────────────────────────────────
  {
    defId: 'gold_dust',
    name: 'Gold Dust',
    description: 'Add +5 chips to a number cell. Fiches placed there gain extra chips.',
    rarity: 'common',
    effect: { type: 'boostChips', cellIdx: 0 },
  },
  {
    defId: 'gilded_rim',
    name: 'Gilded Rim',
    description: 'Add +5 chips to all connected subcells of an outside or dozen section.',
    rarity: 'uncommon',
    effect: { type: 'boostSubcell', cellIdx: 0 },
  },
  {
    defId: 'amplifier',
    name: 'Amplifier',
    description: 'Add +1 mult to any cell. Fiches placed there multiply harder.',
    rarity: 'rare',
    effect: { type: 'boostMult', cellIdx: 0 },
  },
] as const);

export const CONSUMABLES_BY_ID: Readonly<Record<string, ConsumableDef>> = Object.freeze(
  CONSUMABLES.reduce<Record<string, ConsumableDef>>((acc, def) => {
    acc[def.defId] = def;
    return acc;
  }, {}),
);

const CONSUMABLE_SHEET_1: Readonly<Record<string, number>> = {
  double_or_nothing: 0,
  loose_change: 1,
  compound: 2,
  swap_cells: 3,
  flip_color: 4,
  bleach: 5,
};

const CONSUMABLE_SHEET_2: Readonly<Record<string, number>> = {
  merge: 0,
  skip_slot: 1,
  mint: 2,
  loaded_ball: 3,
  shuffle_columns: 4,
  shuffle_rows: 5,
};

const CONSUMABLE_SHEET_3: Readonly<Record<string, number>> = {
  gold_dust: 0,
  gilded_rim: 1,
  amplifier: 2,
};

export function consumableSpritesheet(defId: string): { key: string; frame: number } | undefined {
  const f1 = CONSUMABLE_SHEET_1[defId];
  if (f1 !== undefined) return { key: 'consumables-1', frame: f1 };
  const f2 = CONSUMABLE_SHEET_2[defId];
  if (f2 !== undefined) return { key: 'consumables-2', frame: f2 };
  const f3 = CONSUMABLE_SHEET_3[defId];
  if (f3 !== undefined) return { key: 'consumables-3', frame: f3 };
  return undefined;
}
